import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { FeeTrendChart } from "@/components/FeeTrendChart";
import { get, TYPE_OPTIONS } from "@/lib/api";

const sel = "rounded-full border border-[#CBD5E1] bg-white px-4 py-2 text-sm";

export default function FeeTrends() {
  const [state, setState] = useState("");
  const [type, setType] = useState("");
  const { data: states = [] } = useQuery({ queryKey: ["states"], queryFn: () => get("/states") });
  const { data } = useQuery({ queryKey: ["trends", state, type], queryFn: () => get("/trends", { state: state || undefined, type: type || undefined }) });
  const yrs = data?.by_year || [];
  const change = yrs.length > 1 ? Math.round(((yrs[yrs.length - 1].avg_fee - yrs[0].avg_fee) / yrs[0].avg_fee) * 100) : null;

  return (
    <div data-testid="fee-trends-page">
      <Seo title="Historical HOA Fee Trends" description="See how average HOA fees have changed over time by state and community type." path="/fee-trends" />
      <PageHeader eyebrow="Market data" title="HOA fee trends" subtitle="Average monthly HOA dues across the directory by state and by community type, with year-by-year history as it is reported." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-5 sm:space-y-8">
        <p className="rounded-xl bg-[#E6F4F1] border border-[#63D8C4] p-4 text-sm text-[#0B2150]" data-testid="trends-notice">
          Trends are built from fees on listed communities, including estimated fees that have not been confirmed by the HOA. As more HOAs report fees and history, these charts will improve. <Link to="/submit-hoa" className="font-semibold underline">Submit an HOA's fees</Link>.
        </p>
        <div className="flex flex-wrap gap-2">
          <label className="sr-only" htmlFor="trend-state">State</label>
          <select id="trend-state" className={sel} value={state} onChange={(e) => setState(e.target.value)} data-testid="trends-state-select">
            <option value="">All states</option>{states.map((s) => <option key={s.slug} value={s.slug}>{s.state}</option>)}
          </select>
          <label className="sr-only" htmlFor="trend-type">Community type</label>
          <select id="trend-type" className={sel} value={type} onChange={(e) => setType(e.target.value)} data-testid="trends-type-select">
            <option value="">All community types</option>{TYPE_OPTIONS.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-4 sm:p-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold text-[#0B2150]">Average monthly fee by year</h2>
            {change != null && <p className="text-sm text-slate-600" data-testid="trends-change">{yrs[0].year}-{yrs[yrs.length - 1].year}: <span className="font-display text-2xl font-bold text-[#027A6D]">+{change}%</span></p>}
          </div>
          {yrs.length ? <FeeTrendChart data={yrs} dataKey="avg_fee" height={320} testid="trends-year-chart" /> : <p className="text-sm text-slate-600" data-testid="trends-empty">No historical fee records have been reported yet for this selection.</p>}
        </section>
        <div className="grid gap-5 sm:gap-8 lg:grid-cols-2">
          <section className="rounded-2xl border border-[#E2E8F0] bg-white p-4 sm:p-8">
            <h2 className="text-xl font-semibold text-[#0B2150] mb-6">By community type</h2>
            <div className="h-72" data-testid="trends-type-chart">
              <ResponsiveContainer><BarChart data={data?.by_type || []} layout="vertical" margin={{ left: 10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                <XAxis type="number" tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="type" width={140} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => [`$${v}/mo`, "Average"]} />
                <Bar dataKey="avg_fee" fill="#03A896" radius={[0, 6, 6, 0]} />
              </BarChart></ResponsiveContainer>
            </div>
          </section>
          <section className="rounded-2xl border border-[#E2E8F0] bg-white p-4 sm:p-8">
            <h2 className="text-xl font-semibold text-[#0B2150] mb-6">Highest average fee by state</h2>
            <div className="h-72" data-testid="trends-state-chart">
              <ResponsiveContainer><BarChart data={(data?.by_state || []).slice(0, 15)}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="code" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => [`$${v}/mo`, "Average"]} />
                <Bar dataKey="avg_fee" fill="#0B2150" radius={[6, 6, 0, 0]} />
              </BarChart></ResponsiveContainer>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
